import { type FC, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useQuery } from 'react-query'
import { IconCategories, IconPlusInBox } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { AddCategoryModal } from 'pages/mediaCategories/components/AddCategoryModal'
import { getMediaCategories } from 'services/endpoints/mediaCategory'
import { type CategoryStats, getVaultChatCategoryStats } from 'services/endpoints/vault'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { type ICategory } from 'types/interfaces/ITypes'


import styles from './selectCategoriesModal.module.scss'

interface Props {
  selectedCategories: ICategory[]
  onCancel: () => void
  onSave: (selectedCategories: ICategory[]) => void
  onlyOne?: boolean
  dontClearModal?: boolean
  includeSuggestedCategory?: boolean
  title?: string
  categories?: ICategory[]
  chatUserId?: string
  hidePublicCategories?: boolean
  hideVisibilityBadges?: boolean
  hideAllCategory?: boolean
}

const ALL_ID = 'all'
const SUGGESTED_ID = 'suggested' // keep as string, don’t fake it into a number

const SelectCategoriesModal: FC<Props> = ({
  selectedCategories,
  onCancel,
  onSave,
  onlyOne,
  includeSuggestedCategory,
  title,
  chatUserId,
  hidePublicCategories = false,
  hideVisibilityBadges = false,
  hideAllCategory = false
}) => {
  const { t } = useTranslation()
  const user = useUserProfile()
  const viewerId = user?.id ?? ''

  const profile = useProfileCacheStore((state) =>
    user?.username ? state.getProfile(user.username, viewerId) : undefined
  )

const [selected, setSelected] = useState<ICategory[]>(selectedCategories)
const [addModalOpen, setAddModalOpen] = useState(false)
const [existingNamesArray, setExistingNamesArray] = useState<string[]>([])
const [statsMap, setStatsMap] = useState<Record<string, CategoryStats>>({})
const [search, setSearch] = useState('')
const [debouncedSearch, setDebouncedSearch] = useState('')



const {
  data: searchResponse,
} = useQuery<{ data: ICategory[] }>(
  ['selectMediaCategoriesSearch', debouncedSearch],
  async () =>
    await getMediaCategories({
      search: debouncedSearch
    }),
  {
    enabled: debouncedSearch.length > 0,
    keepPreviousData: true,
    staleTime: 30_000
  }
)
useEffect(() => {
  const timeout = window.setTimeout(() => {
    setDebouncedSearch(search.trim())
  }, 300)

  return () => {
    window.clearTimeout(timeout)
  }
}, [search])


  // Build categories to render (no selection state stored in the category objects themselves)
const rawCategories: ICategory[] = useMemo(() => {
  const normalCategories =
    profile?.all_media_categories ?? []

  const searchedCategories =
    searchResponse?.data ?? []

  const sourceCategories =
    debouncedSearch.length > 0
      ? searchedCategories
      : normalCategories

  const base: ICategory[] = []

  if (includeSuggestedCategory) {
    base.push({
      id: SUGGESTED_ID,
      name: 'Suggested',
      public: 0,
      messages: 0,
      suggestions: null,
      user_id: profile?.id ?? null,
      created_at: '',
      updated_at: ''
    } as unknown as ICategory)
  }

  if (!hideAllCategory) {
    base.push({
      id: ALL_ID,
      name: 'All',
      public: 0,
      messages: 0,
      suggestions: null,
      user_id: profile?.id ?? null,
      created_at: '',
      updated_at: ''
    } as unknown as ICategory)
  }

  const normalized = sourceCategories
    .map((cat: any) => ({ ...cat }))
    .filter((cat: any) => {
      if (!hidePublicCategories) return true

      return Number(cat.public) !== 1
    })

  return [...base, ...normalized]
}, [
  profile?.all_media_categories,
  profile?.id,
  searchResponse?.data,
  debouncedSearch,
  includeSuggestedCategory,
  hidePublicCategories,
  hideAllCategory
])

  // Fetch stats when needed
  useEffect(() => {
    let mounted = true

    const fetchStats = async () => {
      if (!includeSuggestedCategory || !chatUserId) return

      try {
        const list = await getVaultChatCategoryStats(chatUserId)
        if (!mounted) return

        const map: Record<string, CategoryStats> = {}

        list.forEach(row => {
          map[row.id] = row
        })
        setStatsMap(map)
      } catch (e) {
        // silent fail; keep UI working
        if (mounted) setStatsMap({})
        console.error('category stats error', e)
      }
    }

    fetchStats()
    return () => {
      mounted = false
    }
  }, [includeSuggestedCategory, chatUserId])



  // Sorted list (don’t mutate arrays in-place; create new arrays)
  const sortedCategories: ICategory[] = useMemo(() => {
    if (!includeSuggestedCategory) return rawCategories

    const isAll = (c: ICategory) => c.id === ALL_ID
    const isSuggested = (c: ICategory) => String(c.id) === SUGGESTED_ID

    const pinned: ICategory[] = []
    const rest: ICategory[] = []

    for (const c of rawCategories) {
      if (isAll(c) || isSuggested(c)) pinned.push(c)
      else rest.push(c)
    }

    const sortedRest = [...rest].sort((a, b) => {
    const aId = String(a.id)
    const bId = String(b.id)

    const aConv =
      Number(statsMap[aId]?.message_conversion_rate ?? -1)

    const bConv =
      Number(statsMap[bId]?.message_conversion_rate ?? -1)
      if (bConv !== aConv) return bConv - aConv
      return String(a.name).localeCompare(String(b.name))
    })

    const orderedPinned = [
      ...pinned.filter((c) => String(c.id) === SUGGESTED_ID),
      ...pinned.filter((c) => c.id === ALL_ID)
    ]

    return [...orderedPinned, ...sortedRest]
  }, [rawCategories, statsMap, includeSuggestedCategory])

  // Keep selected state in sync with props/rawCategories
  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      const defaultAll = rawCategories.find((cat) => cat.id === ALL_ID)
      if (defaultAll) setSelected([defaultAll])
      else setSelected([])
      return
    }

    setSelected(selectedCategories)
  }, [selectedCategories, rawCategories])

  // Update existing names for AddCategoryModal
  useEffect(() => {
    setExistingNamesArray(rawCategories.map((c) => c.name))
  }, [rawCategories])

const isAllCategory = (c: ICategory) => c.id === ALL_ID

const toggleSelect = (category: ICategory) => {
  setSelected((prev) => {
    const clickedAll = isAllCategory(category)
    const prevHasAll = prev.some(isAllCategory)

    // If user clicks "All", it should become the ONLY selection (unless it's already the only selection)
    if (clickedAll) {
      // If it's already the only selected item, keep it selected (prevents empty state)
      if (prev.length === 1 && prevHasAll) return prev

      // Otherwise, clear everything else and select All
      return [category]
    }

    // From here: user clicked a NON-"All" category
    const exists = prev.some((sel) => sel.id === category.id)

    if (exists) {
      // Remove it
      const next = prev.filter((el) => el.id !== category.id)

      // If nothing left, fall back to All
      if (next.length === 0) {
        const all = rawCategories.find(isAllCategory)
        return all ? [all] : []
      }

      return next
    }

    // Selecting a new non-All category:
    // If "All" was selected, remove it first (All + others not allowed)
    const withoutAll = prevHasAll ? prev.filter((c) => !isAllCategory(c)) : prev

    return onlyOne ? [category] : [...withoutAll, category]
  })
}
  if (addModalOpen) {
    return (
      <div className={styles.addModal}>
        <AddCategoryModal onClose={() => setAddModalOpen(false)} existingNamesArray={existingNamesArray} />
      </div>
    )
  }

  return (
    <div>
      {title && <div className={styles.modalTitle}>{title}</div>}

      <div className={styles.searchWrapper}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Categories"
          className={styles.searchInput}
        />
      </div>

      <div className={styles.list}>
        {sortedCategories.map((category) => {
          const isSelected = selected.some((sel) => sel.id === category.id)
          const isSuggested = String(category.id) === SUGGESTED_ID
          const posts = Number(category.posts ?? category.posts ?? 0)
          const premium = Number(category.premium ?? category.premium ?? 0)
          const stories = Number(category.stories ?? category.stories ?? 0)
          const messages = Number(category.messages ?? category.messages ?? 0)

          // Public/Private label — adjust if your API defines it differently
          const isPublic = category.public === true || category.public === 1 || category.public === '1'

          const visibilityLabel = isPublic ? 'Public' : 'Private'

          const isDefaultCategory = category.id === ALL_ID

          const categoryId = category.id != null
            ? String(category.id)
            : null

          const showStats =
            includeSuggestedCategory &&
            categoryId !== null &&
            categoryId !== ALL_ID &&
            categoryId !== SUGGESTED_ID &&
            !!statsMap[categoryId]

          const s =
            showStats && categoryId
              ? statsMap[categoryId]
              : null
          const likes = s ? Number(s.post_likes || 0) : 0
          const buys = s ? Number(s.message_purchases || 0) : 0
          const convPct = s ? Math.round(Number(s.message_conversion_rate || 0)) : 0

return (
  <ActionCard
    key={String(category.id)}
    hasArrow={false}
    hasRadio={!isSuggested}
    toggleActive={!isSuggested && isSelected}
    clickFn={
      isSuggested
        ? () => undefined
        : () => toggleSelect(category)
    }
    icon={
      <div
        className={`${styles.icon} ${
          isSuggested ? styles.suggestedCategoryIconDisabled : ''
        }`}
      >
        <IconCategories color="#fff" />
      </div>
    }
    text={
      <div className={styles.categoryContent}>
        <div className={styles.categoryHeader}>
          <div className={styles.categoryTitle}>{category.name}</div>

          {isSuggested ? (
            <span className={styles.comingSoon}>
              Coming Soon
            </span>
          ) : (
            (isDefaultCategory || !hideVisibilityBadges) && (
              <span className={styles.visibilityLabel}>
                {isDefaultCategory ? 'Default' : visibilityLabel}
              </span>
            )
          )}
        </div>

        <div className={styles.categoryTags}>
          <>
          {posts > 0 && (
            <div className={styles.categoryTag}>
              <span>{posts}</span>
              {t('posts')}
            </div>
          )}

          {premium > 0 && (
            <div className={styles.categoryTag}>
              <span>{premium}</span>
              {t('premium')}
            </div>
          )}

          {stories > 0 && (
            <div className={styles.categoryTag}>
              <span>{stories}</span>
              {t('stories')}
            </div>
          )}

          {messages > 0 && (
            <div className={styles.categoryTag}>
              <span>{messages}</span>
              {t('messages')}
            </div>
          )}

            {s && (
              <>
                {likes > 0 && (
                  <div className={styles.categoryTag}>
                    <span>{likes}</span>Post Likes
                  </div>
                )}
                {buys > 0 && (
                  <div className={styles.categoryTag}>
                    <span>{buys}</span>Message Purchases
                  </div>
                )}
                {convPct > 0 && (
                  <div className={styles.categoryTag}>
                    <span>{convPct}%</span>Conversion Rate
                  </div>
                )}
              </>
            )}
          </>
        </div>
      </div>
    }
  />
)
        })}

        <ActionCard
          icon={<IconPlusInBox width="22" height="22" />}
          clickFn={() => setAddModalOpen(true)}
          text={t('addNewCategory')}
          addNewText
        />
      </div>

      <div className={styles.selectCategoryFooter}>
        <Button text={t('cancel')} color="grey" width="13" font="mont-14-normal" height="3" clickFn={onCancel} />
        <Button text={t('save')} color="black" width="14" font="mont-14-normal" height="3" clickFn={() => onSave(selected)} />
      </div>
    </div>
  )
}

export default SelectCategoriesModal
